// Home (FD §22.1, §8.2) — answers three questions in order: what needs my
// decision, what are my agents doing right now, and what happened recently.
// While the workspace isn't fully set up, a short checklist leads the way.

import { LiveDuration } from '../components/live-duration'
import { useState, type ReactNode } from 'react'
import { useQueries, useQuery } from '@tanstack/react-query'
import { Link, useNavigate } from '@tanstack/react-router'
import type { Approval, Project, Run } from '@goblic/shared'
import { RunStatus } from '@goblic/shared'
import { approvalsApi, boardApi, runsApi, workspaceApi } from '../lib/api-client'
import { StatusBadge } from '../components/status-badge'
import { runStatusLabel } from '../lib/run-recovery'
import { RunTrackMini } from '../components/run-track'
import { PageHeader, Panel } from '../components/ui'
import { NewProjectDialog, useCanCreateProject } from '../components/projects/new-project-dialog'
import { useWorkspaceId } from '../lib/auth'
import { useLookups } from '../hooks/useLookups'
import { useCurrentProject } from '../lib/project-context'
import { ACTIVE_RUN_STATUSES, FAILED_RUN_STATUSES, runInView } from '../lib/entities'
import { formatDuration, formatRelative, formatRunCost } from '../lib/format'
import { approvalHeadline } from '../components/approval-panel'
import {
  ClayIcon,
  MomentArt,
  RoleAvatar,
  sceneClass,
  type ClayIconName,
} from '../components/goblin-art'

export function DashboardPage() {
  const workspaceId = useWorkspaceId()
  const lookups = useLookups()
  const { project: currentProject, projects } = useCurrentProject()
  const enabled = workspaceId !== ''
  const canCreateProject = useCanCreateProject()
  const [creatingProject, setCreatingProject] = useState(false)

  const summaryQuery = useQuery({
    queryKey: ['dashboard-summary', workspaceId],
    queryFn: () => workspaceApi.getDashboardSummary(workspaceId),
    enabled,
    retry: false,
  })

  const activeRunsQuery = useQuery({
    queryKey: ['runs', 'active', workspaceId],
    queryFn: () =>
      runsApi.list({
        workspaceId,
        status: [...ACTIVE_RUN_STATUSES, RunStatus.AwaitingApproval, RunStatus.Paused],
      }),
    enabled,
    refetchInterval: 10_000,
  })

  const failedRunsQuery = useQuery({
    queryKey: ['runs', 'failed', workspaceId],
    queryFn: () => runsApi.list({ workspaceId, status: FAILED_RUN_STATUSES }),
    enabled,
  })

  const pendingApprovalsQuery = useQuery({
    queryKey: ['approvals', workspaceId, 'pending'],
    queryFn: () => approvalsApi.list({ workspaceId, status: 'pending' }),
    enabled,
    refetchInterval: 30_000,
  })

  const recentRunsQuery = useQuery({
    queryKey: ['runs', 'recent', workspaceId],
    queryFn: () => runsApi.list({ workspaceId, limit: 8 }),
    enabled,
  })

  const approvals = pendingApprovalsQuery.data?.items ?? []
  const activeRuns = activeRunsQuery.data?.items ?? []
  const recentRuns = recentRunsQuery.data?.items ?? []
  const failedCount = failedRunsQuery.data?.items.length ?? 0
  const budgetPercent = summaryQuery.data?.budgetUsedPercent

  const setupSteps = [
    {
      done: lookups.executors.some((e) => e.credentialProfileId),
      label: 'Connect an executor',
      hint: 'Log in to Claude or Codex so agents can run.',
      to: '/executors' as const,
      search: undefined,
    },
    {
      done: lookups.projects.length > 0,
      label: 'Add a project',
      hint: 'A repository with its workflows and boards.',
      // There is no project page to land on yet: the welcome creates one.
      to: '/welcome' as const,
      search: { step: 'project' as const },
    },
    {
      done: lookups.agents.length > 0,
      label: 'Create an agent',
      hint: 'A role plus executor, model and project.',
      // Agents live in a project; before there is one the step is done after "Add a project".
      to: currentProject ? ('/p/$projectKey/agents' as const) : ('/welcome' as const),
      search: currentProject ? undefined : { step: 'project' as const },
      params: currentProject ? { projectKey: currentProject.key } : undefined,
    },
    {
      done: lookups.tasks.length > 0,
      label: 'Give it a task',
      hint: 'Put one on a board, or send it from Plane.',
      to: '/tasks' as const,
      search: undefined,
    },
  ]
  const setupComplete = setupSteps.every((step) => step.done)

  return (
    <div>
      <PageHeader
        title="All projects"
        description="Your projects, what needs your decision, and what your agents are doing now."
        actions={
          <>
            {canCreateProject ? (
              <button type="button" className="btn-primary" onClick={() => setCreatingProject(true)}>
                New project
              </button>
            ) : null}
            {currentProject ? (
              <Link
                to="/p/$projectKey/tasks"
                params={{ projectKey: currentProject.key }}
                search={{ new: true }}
                className={canCreateProject ? 'btn-secondary' : 'btn-primary'}
              >
                New task
              </Link>
            ) : null}
          </>
        }
      />

      <NewProjectDialog open={creatingProject} onClose={() => setCreatingProject(false)} />

      <ProjectsList
        projects={projects}
        onCreate={canCreateProject ? () => setCreatingProject(true) : undefined}
      />

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-[minmax(0,1fr)_300px]">
        <div className="space-y-6">
          <NeedsYou
            approvals={approvals}
            isLoading={pendingApprovalsQuery.isLoading}
            agentName={lookups.agentName}
          />

          <Panel
            title="Running now"
            actions={
              <Link to="/runs" className="text-sm link">
                All runs
              </Link>
            }
            flush
          >
            {activeRuns.length > 0 ? (
              <RunList runs={activeRuns} lookups={lookups} showElapsed />
            ) : (
              <p className="px-5 py-6 text-base text-ink-3">
                {activeRunsQuery.isLoading
                  ? 'Loading…'
                  : 'No agents are working right now.'}
              </p>
            )}
          </Panel>

          <Panel title="Recent runs" flush className={sceneClass('workshop')}>
            {recentRuns.length > 0 ? (
              <RunList runs={recentRuns} lookups={lookups} />
            ) : (
              <p className="px-5 py-6 text-base text-ink-3">
                {recentRunsQuery.isLoading
                  ? 'Loading…'
                  : 'Runs you start will show up here.'}
              </p>
            )}
          </Panel>
        </div>

        <aside className="space-y-6">
          {!setupComplete ? (
            <Panel title="Get set up">
              <MomentArt
                moment="setup"
                size={64}
                className="float-right -mr-1 -mt-1 ml-1"
              />
              <ol className="space-y-3.5">
                {setupSteps.map((step, i) => (
                  <li key={step.label} className="flex gap-3">
                    <span
                      aria-hidden
                      className={`mt-0.5 grid h-5 w-5 shrink-0 place-items-center rounded-full text-xs font-semibold ${
                        step.done
                          ? 'bg-action text-on-action'
                          : 'border border-line-strong text-ink-3'
                      }`}
                    >
                      {step.done ? '✓' : i + 1}
                    </span>
                    <div className="min-w-0">
                      {step.done ? (
                        <p className="text-base text-ink-3 line-through decoration-ink-3/50">
                          {step.label}
                        </p>
                      ) : (
                        <Link
                          to={step.to}
                          search={step.search}
                          params={step.params as never}
                          className="text-base font-medium text-ink hover:text-moss"
                        >
                          {step.label}
                        </Link>
                      )}
                      {!step.done ? (
                        <p className="text-sm text-ink-3">{step.hint}</p>
                      ) : null}
                    </div>
                  </li>
                ))}
              </ol>
            </Panel>
          ) : null}

          <Panel title="At a glance">
            <dl className="space-y-3 text-base">
              <GlanceRow
                icon="gears"
                label="Working"
                value={
                  // A quota-paused run resumes by itself: still working.
                  activeRuns.filter((r) => runInView(r, 'active')).length
                }
              />
              <GlanceRow
                icon="hourglass"
                label="Waiting for you"
                value={approvals.length}
                tone={approvals.length > 0 ? 'signal' : undefined}
                to="/approvals"
              />
              <GlanceRow
                icon="wrench-broken"
                label="Failed"
                value={failedCount}
                tone={failedCount > 0 ? 'danger' : undefined}
                to="/runs"
              />
              <GlanceRow
                icon="coins"
                label="Spent on recent runs"
                value={(() => {
                  const costCents = recentRuns.reduce(
                    (total, run) => total + (run.costCents || 0),
                    0
                  )
                  // Only "Included" when every recent run ran on a subscription — one API-billed run among them is real spend.
                  const billing = recentRuns.length > 0 && recentRuns.every((r) => r.billing === 'subscription') ? 'subscription' : 'api'
                  const cost = formatRunCost({ costCents, billing }, lookups.runCostDisplay)
                  return <span title={cost.tooltip}>{cost.text}</span>
                })()}
              />
            </dl>
            {budgetPercent != null ? (
              <BudgetMeter percent={budgetPercent} />
            ) : null}
          </Panel>
        </aside>
      </div>
    </div>
  )
}

/** The workspace's projects: key, name, open tasks, and a link to each overview. */
function ProjectsList({
  projects,
  onCreate,
}: {
  projects: Project[]
  /** Present when the user may create projects; drives the empty state. */
  onCreate?: () => void
}) {
  const overviews = useQueries({
    queries: projects.map((project) => ({
      queryKey: ['project', project.id, 'overview'],
      queryFn: () => boardApi.overview(project.id),
      staleTime: 15_000,
    })),
  })
  if (projects.length === 0) {
    if (!onCreate) return null
    return (
      <div className="mb-6">
        <Panel title="Projects">
          <div className="flex flex-col items-start gap-3">
            <p className="text-base text-ink-2">
              No projects yet. A project is one codebase with its workflows and boards.
            </p>
            <button type="button" className="btn-primary" onClick={onCreate}>
              New project
            </button>
          </div>
        </Panel>
      </div>
    )
  }
  return (
    <div className="mb-6">
      <Panel title="Projects" flush>
        <ul className="divide-y divide-line">
          {projects.map((project, index) => {
            const overview = overviews[index]?.data
            const open = overview?.streams.reduce((sum, s) => sum + s.openCount, 0)
            return (
              <li key={project.id}>
                <Link
                  to="/p/$projectKey/overview"
                  params={{ projectKey: project.key }}
                  className="flex items-center gap-4 px-5 py-3 transition-colors hover:bg-sunken/60"
                >
                  <span className="w-14 shrink-0 font-mono text-sm font-semibold text-moss-strong">
                    {project.key}
                  </span>
                  <span className="min-w-0 flex-1 truncate text-base font-medium text-ink">
                    {project.name}
                  </span>
                  <span className="shrink-0 text-sm text-ink-3">
                    {open == null ? '' : open === 1 ? '1 open task' : `${open} open tasks`}
                  </span>
                </Link>
              </li>
            )
          })}
        </ul>
      </Panel>
    </div>
  )
}

function NeedsYou({
  approvals,
  isLoading,
  agentName,
}: {
  approvals: Approval[]
  isLoading: boolean
  agentName: (id: string) => string
}) {
  if (isLoading) return null
  if (approvals.length === 0) {
    return (
      <div className="flex items-center gap-3 rounded-panel border border-line bg-surface px-5 py-4">
        <MomentArt moment="empty" size={56} className="-my-2" />
        <span aria-hidden className="h-2 w-2 shrink-0 rounded-full bg-ok" />
        <p className="text-base text-ink-2">
          Nothing is waiting on you. Agents will ask here before they push code,
          open a pull request or go over budget.
        </p>
      </div>
    )
  }
  return (
    <section className="overflow-hidden rounded-panel border border-signal/40 bg-surface">
      <header className="flex items-center justify-between gap-4 border-b border-signal/25 bg-signal-soft px-5 py-3">
        <MomentArt moment="waiting" size={56} className="-my-2" />
        <h2 className="mr-auto font-display text-md font-semibold text-ink">
          {approvals.length === 1
            ? '1 decision waiting on you'
            : `${approvals.length} decisions waiting on you`}
        </h2>
        <Link to="/approvals" className="text-sm link">
          Open approvals
        </Link>
      </header>
      <ul className="divide-y divide-line">
        {approvals.slice(0, 5).map((approval) => (
          <li key={approval.id}>
            <Link
              to="/approvals"
              search={{ id: approval.id }}
              className="flex items-center justify-between gap-4 px-5 py-3 transition-colors hover:bg-sunken/60"
            >
              <div className="min-w-0">
                <p className="truncate text-base font-medium text-ink">
                  {approvalHeadline(approval)}
                </p>
                <p className="truncate text-sm text-ink-3">
                  {approval.agentId ? agentName(approval.agentId) : 'Workflow'}{' '}
                  asked{' '}
                  {formatRelative(approval.createdAt)}
                </p>
              </div>
              <div className="flex shrink-0 items-center gap-4">
                <StatusBadge status={approval.riskIndicator} />
                <span className="btn-secondary btn-sm">Review</span>
              </div>
            </Link>
          </li>
        ))}
      </ul>
    </section>
  )
}

function RunList({
  runs,
  lookups,
  showElapsed,
}: {
  runs: Run[]
  lookups: ReturnType<typeof useLookups>
  showElapsed?: boolean
}) {
  const navigate = useNavigate()
  return (
    <ul className="divide-y divide-line">
      {runs.map((run) => {
        return (
          <li key={run.id}>
            <button
              type="button"
              onClick={() =>
                navigate({ to: '/runs/$runId', params: { runId: run.id } })
              }
              className="grid w-full grid-cols-[minmax(0,1fr)_auto] items-center gap-x-6 gap-y-1 px-5 py-3 text-left transition-colors hover:bg-sunken/60 sm:grid-cols-[minmax(0,1fr)_150px_110px]"
            >
              <span className="flex min-w-0 items-center gap-3">
                <RoleAvatar roleName={lookups.roleName(run.roleId)} size={32} />
                <span className="min-w-0">
                  <span className="block truncate text-base font-medium text-ink">
                    {lookups.taskTitle(run.taskId)}
                  </span>
                  <span className="block truncate text-sm text-ink-3">
                    <span className="text-action">{lookups.agentName(run.agentId)}</span>
                    {run.projectId ? (
                      <> in <span className="text-signal">{lookups.projectName(run.projectId)}</span></>
                    ) : null}
                  </span>
                </span>
              </span>
              <span className="flex flex-col items-end gap-1 sm:items-start">
                <RunTrackMini
                  status={run.status}
                  hasStarted={run.startedAt != null}
                />
                <StatusBadge status={run.status} label={runStatusLabel(run)} />
              </span>
              <span className="hidden text-right text-sm text-ink-3 sm:block">
                {showElapsed && run.startedAt
                  ? <LiveDuration startedAt={run.startedAt} endedAt={run.endedAt} />
                  : formatRelative(run.queuedAt)}
              </span>
            </button>
          </li>
        )
      })}
    </ul>
  )
}

function GlanceRow({
  icon,
  label,
  value,
  tone,
  to,
}: {
  icon: ClayIconName
  label: string
  value: ReactNode
  tone?: 'signal' | 'danger'
  to?: '/approvals' | '/runs'
}) {
  const valueClass =
    tone === 'signal'
      ? 'text-signal'
      : tone === 'danger'
        ? 'text-danger'
        : 'text-ink'
  const content = (
    <>
      <dt className="flex items-center gap-2.5 text-ink-2">
        <ClayIcon name={icon} size={24} />
        {label}
      </dt>
      <dd className={`font-semibold tabular-nums ${valueClass}`}>{value}</dd>
    </>
  )
  return to && tone ? (
    <Link to={to} className="flex items-center justify-between hover:underline">
      {content}
    </Link>
  ) : (
    <div className="flex items-center justify-between">{content}</div>
  )
}

function BudgetMeter({ percent }: { percent: number }) {
  // FD §25: 80% warns, 100% pauses or ends runs per policy (server-side).
  const tone =
    percent >= 100 ? 'bg-danger' : percent >= 80 ? 'bg-signal' : 'bg-moss'
  return (
    <div className="mt-5 border-t border-line pt-4">
      <div className="flex items-baseline justify-between text-base">
        <span className="text-ink-2">Workspace budget</span>
        <span className="font-semibold text-ink">{percent}% used</span>
      </div>
      <div className="mt-2 h-1.5 w-full overflow-hidden rounded-full bg-sunken">
        <div
          className={`h-full rounded-full ${tone}`}
          style={{ width: `${Math.min(100, percent)}%` }}
        />
      </div>
      {percent >= 80 ? (
        <p className="mt-2 text-sm text-ink-3">
          {percent >= 100
            ? 'Runs are paused until the budget is raised.'
            : 'Close to the limit. Runs pause at 100%.'}
        </p>
      ) : null}
    </div>
  )
}
